import { metrics, trace } from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchLogRecordProcessor,
  ConsoleLogRecordExporter,
  LoggerProvider,
} from '@opentelemetry/sdk-logs';
import {
  ConsoleMetricExporter,
  MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics';
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
} from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

const SERVICE_NAME = 'wms-front';
const INSTRUMENTATION_NAME = 'wms-front-observability';

export interface IOtelInitOptions {
  /** URL base OTLP HTTP (ex.: http://localhost:4318). Se omitido → exporters console. */
  otlpEndpoint?: string;
  serviceName?: string;
}

interface IOtelRuntime {
  tracerProvider: WebTracerProvider;
  meterProvider: MeterProvider;
  loggerProvider: LoggerProvider;
}

let runtime: IOtelRuntime | null = null;

function resolveEndpoint(): string | undefined {
  const fromRuntime = window.__RUNTIME_CONFIG__?.OTEL_OTLP_ENDPOINT?.trim();
  const fromEnv = import.meta.env.VITE_OTEL_OTLP_ENDPOINT?.trim();
  return fromRuntime || fromEnv || undefined;
}

/** Inicializa SDK OTel uma vez (idempotente). */
export function initOpenTelemetry(options: IOtelInitOptions = {}): IOtelRuntime {
  if (runtime) return runtime;

  const serviceName = options.serviceName ?? SERVICE_NAME;
  const otlpEndpoint = options.otlpEndpoint ?? resolveEndpoint();
  const base = otlpEndpoint?.replace(/\/$/, '') ?? '';
  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: serviceName,
  });

  const spanExporter = otlpEndpoint
    ? new OTLPTraceExporter({ url: `${base}/v1/traces` })
    : new ConsoleSpanExporter();

  const metricExporter = otlpEndpoint
    ? new OTLPMetricExporter({ url: `${base}/v1/metrics` })
    : new ConsoleMetricExporter();

  const logExporter = otlpEndpoint
    ? new OTLPLogExporter({ url: `${base}/v1/logs` })
    : new ConsoleLogRecordExporter();

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(spanExporter)],
  });
  tracerProvider.register();

  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: metricExporter,
        exportIntervalMillis: otlpEndpoint ? 60_000 : 15_000,
      }),
    ],
  });
  metrics.setGlobalMeterProvider(meterProvider);

  const loggerProvider = new LoggerProvider({
    resource,
    processors: [new BatchLogRecordProcessor({ exporter: logExporter })],
  });
  logs.setGlobalLoggerProvider(loggerProvider);

  runtime = { tracerProvider, meterProvider, loggerProvider };
  return runtime;
}

export function getOtelRuntime(): IOtelRuntime | null {
  return runtime;
}

export function getOtelTracer() {
  initOpenTelemetry();
  return trace.getTracer(INSTRUMENTATION_NAME);
}

export function getOtelMeter() {
  initOpenTelemetry();
  return metrics.getMeter(INSTRUMENTATION_NAME);
}

export function getOtelLogger() {
  initOpenTelemetry();
  return logs.getLogger(INSTRUMENTATION_NAME);
}

export async function flushOpenTelemetry(): Promise<void> {
  if (!runtime) return;
  await Promise.allSettled([
    runtime.tracerProvider.forceFlush(),
    runtime.meterProvider.forceFlush(),
    runtime.loggerProvider.forceFlush(),
  ]);
}

export async function shutdownOpenTelemetry(): Promise<void> {
  if (!runtime) return;
  const current = runtime;
  runtime = null;
  await Promise.allSettled([
    current.tracerProvider.shutdown(),
    current.meterProvider.shutdown(),
    current.loggerProvider.shutdown(),
  ]);
}
